import { Injectable } from '@angular/core';
import { AppService } from '@shared/services/core/app.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { APIResponse, MFAClientIdPayload, MFAPreferMasterPayload, MFAQRCodePayload } from '@shared/models/auth.interface';
import { map } from 'rxjs';
import { SessionService } from './session.service';
import { retrySendCodepayload, SavePreferencesPayload, UserAccLockedPayload, verifyOTPPayload } from '@shared/models/mfa-prefrences.interface';
import { EncryptionDecryptionService } from '@shared/services/core/encryption-decryption.service';
import { RSAService } from '@shared/services/core/rsa.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthMFAService {
  private _clientPortalAPIDomain!: string;

  constructor(private _appService: AppService, private http: HttpClient, private _sessionService: SessionService, 
    private _encrDecryption:EncryptionDecryptionService, private rsaEncr:RSAService) {
    this._clientPortalAPIDomain = this._appService.getAppConfigs()["CLIENTPORTAL"].BaseUrl;
  }

  getClientMFADetials(payload: MFAClientIdPayload) {
    const getClientMFA = this._appService.getAppConfigs()["CLIENTPORTAL"].Versions.V1["CLIENTMFA"]["GetClientMFA"].PATH;
    const url = this._clientPortalAPIDomain + getClientMFA + '?' + this.prepareQueryParameters(payload);
    const headers = new HttpHeaders({'X-Client-Context': environment.appMetaRef});
    return this.http.get<APIResponse<any>>(url,{ headers: headers });
  }

  getMfaQrCode(payload: MFAQRCodePayload) {
    const getMFAQR = this._appService.getAppConfigs()["CLIENTPORTAL"].Versions.V1["MFAQRCode"]["GetMFAQRCode"].PATH;
    const url = this._clientPortalAPIDomain + getMFAQR + '?' + this.prepareQueryParameters(payload);
    return this.http.get<APIResponse<any>>(url).pipe(map((response: { Data: any; }) => response.Data));

  }

  getMFAPreferences(payload: MFAPreferMasterPayload, jti: string, token: string) {
    const getMFApref = this._appService.getAppConfigs()["CLIENTPORTAL"].Versions.V1["MFAPreferences"]["GetMFAPref"].PATH;
    const url = this._clientPortalAPIDomain + getMFApref + '?' + this.prepareQueryParameters(payload);
    var uniqueCode = this._sessionService.getUkRef(jti);
    var publickKey = this._sessionService.getJtiPkr(jti);
    var latestTs =this._sessionService.getTimeStampValue();
    var newKey =this._encrDecryption.generateSecretKey(8);
    var header1 =this._encrDecryption.encryptData((jti+"::"+uniqueCode),newKey);
    var header2 =this._encrDecryption.encryptData((uniqueCode+"::"+latestTs),newKey);
    var header3 =this.rsaEncr.encryptWithPublicKey(newKey, publickKey);
    const authHeaders = new HttpHeaders({ 'Authorization': `Bearer ${token}`, 'X-Er-Type': `${jti}`, 
    'X-PX-SIG-991C': `${header1}`, 'X-TK-PULSE-18C9': `${header2}`, 'X-RSA-FEED-09AF': `${header3}` });
    //const authHeaders = new HttpHeaders({ 'Authorization': `Bearer ${token}`, 'X-Er-Type': `${jti}` });
    return this.http.get<APIResponse<any>>(url, { headers: authHeaders }).pipe(map((response: { Data: any; }) => response.Data));

  }


  SaveMFAPrefrences(requestBody: SavePreferencesPayload, jti: string, token: string) {
    const savePref = this._appService.getAppConfigs()["CLIENTPORTAL"].Versions.V1["SaveUserMFAPrefrences"]["MFASave"].PATH;
    const url = this._clientPortalAPIDomain + savePref;
    var uniqueCode = this._sessionService.getUkRef(jti);
    var publickKey = this._sessionService.getJtiPkr(jti);
    var latestTs =this._sessionService.getTimeStampValue();
    var newKey =this._encrDecryption.generateSecretKey(8);
    var header1 =this._encrDecryption.encryptData((jti+"::"+uniqueCode),newKey);
    var header2 =this._encrDecryption.encryptData((uniqueCode+"::"+latestTs),newKey);
    var header3 =this.rsaEncr.encryptWithPublicKey(newKey, publickKey);
    const ApiHeaders = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'X-Er-Type': `${jti}`,
      'X-PX-SIG-991C': `${header1}`, 
      'X-TK-PULSE-18C9': `${header2}`, 
      'X-RSA-FEED-09AF': `${header3}`
    });
    return this.http.post<APIResponse<boolean>>(url, requestBody, { headers: ApiHeaders });
  }


  retryAuthMFA(requestBody: retrySendCodepayload) {
    const retryAuth = this._appService.getAppConfigs()["CLIENTPORTAL"].Versions.V1["MFARetryAUTH"]["retryAuth"].PATH;
    const url = this._clientPortalAPIDomain + retryAuth;
    const ApiHeaders = new HttpHeaders({ 'Content-Type': 'application/json', 'X-Client-Context': environment.appMetaRef });
    return this.http.post<APIResponse<any>>(url, requestBody, { headers: ApiHeaders });
  }

  VerifyAppOTP(requestBody: verifyOTPPayload, jti: string, token: string) {
    const savePref = this._appService.getAppConfigs()["CLIENTPORTAL"].Versions.V1["MFAverifyOTP"]["verifyOtp"].PATH;
    const url = this._clientPortalAPIDomain + savePref;
    const ApiHeaders = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'X-Er-Type': `${jti}`
    });
    return this.http.post<APIResponse<boolean>>(url, requestBody, { headers: ApiHeaders });
  }

  UserAccLocked(requestBody: UserAccLockedPayload) {
    const userAccLocked = this._appService.getAppConfigs()["CLIENTPORTAL"].Versions.V1["AccountLockMail"]["UserAccLocked"].PATH;
    const url = this._clientPortalAPIDomain + userAccLocked;
    return this.http.post<APIResponse<any>>(url, requestBody);
  }

  private prepareQueryParameters(payload: Object): string {
    let queryParameters = [];
    for (const keyValue of Object.entries(payload)) {
      const parameter = keyValue[0];
      const value = keyValue[1];
      queryParameters.push(`${parameter}=${value}`);
    };
    return queryParameters.join('&');
  }
}
